import React, { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { v4 } from 'uuid';
import { CanBackToResultState, DataExtendState, PlaceRelatedState, UserState } from '../../appState';
import { CommonConfig, LinkIconConfig } from '../../config';
import { RouterConfig } from '../../config/routerConfig';
import { UrlConfig } from '../../config/urlConfig';
import { AppData } from '../../data';
import { CodeEnum, GeometryTypeEnum, MapTypeEnum, PlaceExtensionTypeEnum, TypeRequestUpdateEnum } from '../../enum';
import { Resource } from '../../resource';
import { ApiTool, AppTool, MapTool, PlaceTool, SidebarTool, StringTool, UrlTool, UserTool } from '../../tool';
import { useQuery } from '../../useHooks';
import DescriptionDisplay from '../common/descriptionDisplay/descriptionDisplay';
import DirectionButton from '../common/directionButton/directionButton';
import DisplayBusinessHour from '../common/displayBusinessHour/displayBusinessHour';
import DisplayLink from '../common/displayLink/displayLink';
import DisplayMedia from '../common/displayMedia/displayMedia';
import DisplayVr360 from '../common/displayVr360/displayVr360';
import Error from '../common/error/error';
import RequestUpdateBtn from '../common/requestUpdateBtn/requestUpdateBtn';
import SaveButton from '../common/saveButton/saveButton';
import ShareButton from '../common/shareButton/shareButton';
import SwipeableViewsImages from '../common/swipeableViewsImages/swipeableViewsImages';
import ListItemLink from '../listItemLink/listItemLink';
import ListPlaceRelated from '../listPlaceRelated/listPlaceRelated';
import PlaceRelatedV2 from '../placeRelatedV2/placeRelatedV2';
import ShowPositionSelect from '../showPositionSelect/showPositionSelect';
import ShowSuggestPlace from '../showSuggestPlace/showSuggestPlace';
import { MapTypeAppState } from './../../appState/mapTypeAppState';
import { GeoJsonTool } from './../../tool/geoJsonTool';
import DisplayGroup from './../common/displayGroup/displayGroup';
import './placeDetail.scss';

const worldCoords = [[-180, -90], [-180, 90], [180, 90], [180, -90], [-180, -90]]
const PADDING_DEFAULT = 420

function PlaceDetail(props) {
  const { id, type, lat, lng, data } = useParams()
  const { text } = useQuery()
  const userState = useRecoilValue(UserState)
  const history = useHistory()
  const location = useLocation()
  const [place, setPlace] = useState(null)
  const polygonRef = useRef(null)
  const polylineRef = useRef(null)
  const polygonsRef = useRef([])
  const polylinesRef = useRef([])

  const setMapTypeAppState = useSetRecoilState(MapTypeAppState)
  const [dataExtendState, setDataExtendState] = useRecoilState(DataExtendState)
  const [placeRelatedState, setPlaceRelatedState] = useRecoilState(PlaceRelatedState)
  const setCanBackToResultState = useSetRecoilState(CanBackToResultState)

  const polygonHoleRef = useRef(null)

  const currentPlace = useRef(null)
  const [keySaved, setKeySaved] = useState(() => {
    return v4()
  })

  const [loading, setLoading] = useState(false)
  const [links, setLinks] = useState([])
  const [vr360, setVr360] = useState([])

  useEffect(() => {
    if ((type || text) && id) {
      setCanBackToResultState({
        show: true,
        text: text,
        type: type
      })
    }
    return () => {
      setCanBackToResultState(null)
    }
  }, [type, text, id])

  useEffect(() => {
    AppTool.setSearchBarText(place?.name || "")
  }, [place?.name])

  useEffect(() => {
    var sourcePlaces
    var apiSaveHistory
    if (id) {
      AppTool.setSearchBarLoading(true)
      SidebarTool.setOpen(true)
      setLoading(true)
      sourcePlaces = ApiTool.get(`${UrlConfig.poi.detail}?place_id=${id}`, (res) => {
        setLoading(false)
        AppTool.setSearchBarLoading(false)
        if (res?.status == CodeEnum.OK) {
          let typeCheck = PlaceTool.isAreaOrStreet(res?.result)
          setMarkerAndShowInfo(res?.result)
          drawPoly(res?.result)
          setPlace(res?.result)
          // res?.result?.object?.id && AppData.map.setSelectedBuildings([res?.result?.object?.id])
          if (!typeCheck && !location.state?.disableMoveMap) {
            moveCamera(res?.result)
          }
          else {
            let projection = AppData.map.getProjection()
            let point = projection.fromLatLngToContainerPixel(res?.result?.geometry?.location)
            if (point?.x <= PADDING_DEFAULT) {
              moveCamera(res?.result)
            }
          }
          let classifyMetaData = PlaceTool.classifyMetaData(res?.result?.metadata)
          let placeExtend = PlaceTool.convertClassifyMetaDataToPlaceExTend(classifyMetaData)
          let links = placeExtend?.link.sort((a, b) => a.order - b.order)
          let vr360 = placeExtend?.vr360.sort((a, b) => a.order - b.order)
          setLinks(links)
          setVr360(vr360)
        }
        else {
          setPlace(null)
        }
      })
    }

    return () => {
      currentPlace.current?.setMap(null)
      polylineRef.current?.setMap(null)
      polygonRef.current?.setMap(null)
      polygonsRef.current?.forEach(polygon => {
        polygon?.setMap(null)
      })
      polylinesRef.current?.forEach(polyline => {
        polyline?.setMap(null)
      })
      polygonHoleRef.current?.setMap(null)
      sourcePlaces?.cancel()
      // AppData.map.setSelectedBuildings([])
    }
  }, [id])

  useEffect(() => {
    let api
    if (id && userState?.place_id) {
      let body = {
        placeId: id
      }
      api = ApiTool.post(UrlConfig.searchHistory.save, body, (res) => {
      }, true)
    }
    return () => {
      api?.cancel()
    }
  }, [[id, userState?.place_id]])

  useEffect(() => {
    var sourceLocation
    if (StringTool.isNumeric(lat) && StringTool.isNumeric(lng)) {
      let numLat = parseFloat(lat)
      let numLng = parseFloat(lng)
      if (-90 <= numLat && numLat <= 90 && -180 <= numLng && numLng <= 180) {
        let placeTemp = {
          location: {
            lat: numLat,
            lng: numLng
          },
          name: `${numLat.toFixed(6)}, ${numLng.toFixed(6)}`
        }
        let body = {
          latlng: `${numLat},${numLng}`,
        }
        setMarkerAndShowInfo(placeTemp)
        moveCamera(placeTemp)
        setPlace(placeTemp)
        AppTool.setSearchBarLoading(true)
        SidebarTool.setOpen(true)
        setLoading(true)
        sourceLocation = ApiTool.queryGetFromJson(UrlConfig.geocode.geocode, body, (res) => {
          setLoading(false)
          AppTool.setSearchBarLoading(false)
          if (res?.status == CodeEnum.OK) {
            let newPlace = {
              ...placeTemp,
              formatted_address: res?.result.formatted_address
            }
            setPlace(newPlace)
          }
          else {
            setPlace(null)
          }
        })

      }
    }
    return () => {
      currentPlace.current?.setMap(null)
      sourceLocation?.cancel()
    }
  }, [lat, lng])

  useEffect(() => {
    setDataExtendState({
      show: false,
      dataExtend: [],
      indexSelect: 0,
      typeData: PlaceExtensionTypeEnum.none
    })
  }, [id])

  useEffect(() => {
    let placeCustom = null
    try {
      placeCustom = JSON.parse(decodeURIComponent(data))
    } catch (error) {

    }
    setMarkerAndShowInfo(placeCustom)
    moveCamera(placeCustom)
    setPlace(placeCustom)
    SidebarTool.setOpen(true)
  }, [data])

  const setMarkerAndShowInfo = (place) => {
    if (place) {
      if (place?.geometry?.location) {
        currentPlace.current = new ngm.map.Marker({
          position: place?.geometry?.location,
          icon: { size: { height: 30, width: 24 }, url: LinkIconConfig.placeDetail.marker },
          title: place?.name,
          zIndex: 99999
        })
        currentPlace?.current.setMap(AppData.map)
      }
      if (place?.location) {
        currentPlace.current = new ngm.map.Marker({
          position: place?.location,
          icon: { size: { height: 30, width: 24 }, url: LinkIconConfig.placeDetail.marker },
          title: place?.name,
          zIndex: 99999
        })
        currentPlace?.current.setMap(AppData.map)
      }
    }
  }

  const moveCamera = (place) => {
    if (place && place?.location) {
      let padding = {
        top: 10,
        left: PADDING_DEFAULT,
        right: 10,
        bottom: 10
      }
      MapTool.moveWithPadding(AppData.map, place?.location, padding, { animate: true })
    }
  }

  const drawPolyline = (data) => {
    polylineRef.current = new ngm.map.Polyline({
      path: data || [],
      strokeColor: "#3335DF",
      strokeOpacity: 1.0,
      strokeWidth: 5,
      userInteractionEnabled: false,
    })
    polylineRef.current.setMap(AppData.map)
  }

  const drawPolygon = (data) => {
    // polygonRef.current = new ngm.map.Polygon({
    //   paths: data || [],
    //   fillColor: "#000000",
    //   fillOpacity: 0.2,
    //   strokeWidth: 1,
    //   userInteractionEnabled: false,
    // })
    // polygonRef.current.setMap(AppData.map)

    polygonHoleRef.current = new ngm.map.Polygon({
      paths: [worldCoords, data[0]],
      fillColor: "#000000",
      fillOpacity: 0.2,
      strokeWidth: 1,
      userInteractionEnabled: false,
    })
    polygonHoleRef.current.setMap(AppData.map)
  }

  const drawMultiPolyline = (data) => {
    if (data && data.length > 0) {
      polylinesRef.current = []
      for (let i = 0; i < data.length; i++) {
        let polyline = new ngm.map.Polyline({
          path: data[i],
          strokeColor: CommonConfig.strokeBlue,
          strokeOpacity: 1,
          strokeWidth: 7,
          userInteractionEnabled: false,
        })
        polyline.setMap(AppData.map)
        polylinesRef.current.push(polyline)
      }
    }
  }

  const drawMultiPolygon = (data) => {
    // if (data && data.length > 0) {
    //   polygonsRef.current = []
    //   for (let i = 0; i < data.length; i++) {
    //     let polygon = new ngm.map.Polygon({
    //       paths: data[i],
    //       fillColor: "#000000",
    //       fillOpacity: 0.2,
    //       strokeWidth: 1,
    //       userInteractionEnabled: false,
    //     })
    //     polygon.setMap(AppData.map)
    //     polygonsRef.current.push(polygon)
    //   }
    // }
    if (data && data.length > 0) {
      let holePaths = []
      for (let i = 0; i < data.length; i++) {
        holePaths.push(data[i][0])
      }
      polygonHoleRef.current = new ngm.map.Polygon({
        paths: [worldCoords, ...holePaths],
        fillColor: "#000000",
        fillOpacity: 0.2,
        strokeWidth: 1,
        userInteractionEnabled: false,
      })
      polygonHoleRef.current.setMap(AppData.map)
    }
  }

  const drawPoly = (place) => {
    if (place) {
      if (place?.geometry?.type) {
        let geometry = place?.geometry
        switch (geometry.type) {
          case GeometryTypeEnum.lineString:
            drawPolyline(geometry.coordinates)
            break
          case GeometryTypeEnum.polygon:
            drawPolygon(geometry.coordinates || [])
            break
          case GeometryTypeEnum.multiLineString:
            drawMultiPolyline(geometry.coordinates || [])
            break
          case GeometryTypeEnum.multiPolygon:
            drawMultiPolygon(geometry.coordinates || [])
            break
        }
        let bound = new ngm.map.LatLngBounds()
        let points = GeoJsonTool.getPointForBound(geometry.coordinates, geometry.type)
        if (points?.length > 0) {
          for (let i = 0; i < points.length; i++) {
            bound.extend(points[i])
          }
          let paddingOptions = {
            top: 10,
            bottom: 10,
            left: CommonConfig.widthSearchBar + (CommonConfig.padding * 2) + 10,
            right: 10
          }

          MapTool.moveWithPadding(AppData.map, geometry.location, paddingOptions)
        }
      }
    }
  }

  const handleBackToResult = () => {
    history.push({
      pathname: RouterConfig.place.search.replace("/:type?", type ? "/" + type : ""),
      search: UrlTool.createSearch({ text: text })
    })
  }

  const onChangeCountSaveGroup = () => {
    setKeySaved(v4())
  }

  const user = UserTool.getUser()

  let urlWebSite = place?.website || ""
  if (!/^(http|https):/.test(urlWebSite)) {
    urlWebSite = 'http://' + urlWebSite
  }

  let isStreet = PlaceTool.isStreet(place)
  let isBoundary = PlaceTool.isArea(place)

  let typeRequestUpdate = isStreet ? TypeRequestUpdateEnum.street : (isBoundary ? TypeRequestUpdateEnum.boundary : TypeRequestUpdateEnum.poi)

  return (
    <div className='placeDetailCpn'>
      <div className='boxContainer' key={place?.place_id || v4()}>
        <div className='boxInfo'>
          {
            !loading &&
            (
              !place ?
                <div className='boxDataNotFound'>
                  <Error message={Resource.message.dataNotFound} subMessage={Resource.message.dataDoesNotExist} />
                </div>
                :
                <>
                  <div className='boxAvatarPlace'>
                    <SwipeableViewsImages disableSwitch place={place} paddingImages={0} sizeButton={30} />
                  </div>

                  <div className='mainInfo'>
                    <div className='textName' title={place?.name}>
                      {place?.name}
                    </div>
                    {
                      place?.types?.length > 0 &&
                      <div className='textDescription'>
                        {place?.types[0] || ''}
                      </div>
                    }
                  </div>

                  <div className='boxLine'></div>

                  <div className='boxButtonOption'>
                    <div className='itemOption'>
                      <DirectionButton latlng={place?.location} />
                    </div>

                    {
                      place?.place_id &&
                      <div className='itemOption'>
                        <SaveButton id={place?.place_id} onChange={onChangeCountSaveGroup} />
                      </div>
                    }
                    <div className='itemOption'>
                      <ShareButton url={location.href} />
                    </div>
                  </div>

                  <div className='boxLine'></div>

                  <DisplayGroup id={place?.place_id} placeName={place?.name} key={keySaved} />

                  <div className='extraInfo'>
                    <div className="listInformation">
                      {
                        place?.formatted_address &&
                        <div className='listItemInfo'>
                          <div className='listItemIcon'>
                            <img src={LinkIconConfig.placeDetail.location} alt='icon location' width={24} height={24} />
                          </div>
                          <div className='listItemText'>
                            {place?.formatted_address}
                          </div>
                        </div>
                      }

                      {
                        place?.businessHours?.length > 0 &&
                        <div className='listItemInfo'>
                          <div className='listItemIcon positionClock'>
                            <img src={LinkIconConfig.placeDetail.clock} alt='icon location' width={24} height={24} />
                          </div>
                          <div className='listItemText'>
                            <DisplayBusinessHour businessHours={place?.businessHours} />
                          </div>
                        </div>
                      }

                      {
                        place?.phoneNumber &&
                        <div className='listItemInfo'>
                          <div className='listItemIcon'>
                            <img src={LinkIconConfig.placeDetail.phone} alt='icon location' width={24} height={24} />
                          </div>
                          <div className='listItemText'>
                            {place?.phoneNumber}
                          </div>
                        </div>
                      }

                      {
                        place?.website &&
                        <div className='listItemInfo'>
                          <div className='listItemIcon'>
                            <img src={LinkIconConfig.placeDetail.global} alt='icon location' width={24} height={24} />
                          </div>
                          <div className='listItemText'>
                            <a href={urlWebSite} target='_blank'>{place?.website}</a>
                          </div>
                        </div>
                      }

                      {
                        user && place?.place_id &&
                        <div className='boxRequestUpdate'>
                          <RequestUpdateBtn id={place?.place_id} type={typeRequestUpdate} placeName={place?.name} isButton typeGeo={place?.geometry?.type} />
                        </div>
                      }
                    </div>
                  </div>

                  {
                    place?.description &&
                    <>
                      <div className='boxLine'></div>
                      <div className='boxItemInfo'>
                        <div className='boxHeader'>
                          {Resource.common.introduce.toUpperCase()}
                        </div>
                        <div className='boxContent'>
                          <DescriptionDisplay value={place?.description} />
                        </div>
                      </div>
                    </>

                  }

                  {
                    place?.mediaFiles?.length > 0 &&
                    <>
                      <div className='boxLine'></div>
                      <div className='boxInfoMediaFiles'>
                        <div className='boxHeader'>
                          {Resource.placeDetail.photoVideo}
                        </div>
                        <div className='boxContent'>
                          <DisplayMedia place={place} />
                        </div>
                      </div>
                    </>
                  }

                  {
                    links?.length > 0 &&
                    <>
                      <div className='boxLine'></div>
                      <div className='boxInfoCus'>
                        <DisplayLink data={links} />
                      </div>
                    </>
                  }

                  {
                    vr360?.length > 0 &&
                    <>
                      <div className='boxLine'></div>
                      <div className='boxContent'>
                        <DisplayVr360 data={vr360} />
                      </div>
                    </>
                  }

                  {
                    place?.object &&
                    <>
                      <div className='boxLine'></div>
                      <div className='boxItemInfo boxDetailPlaceRelated'>
                        <PlaceRelatedV2 data={place?.otherPlaceInObjects} object={place?.object} />
                      </div>
                    </>

                  }
                </>
            )
          }
        </div>
      </div>

      {
        dataExtendState.typeData == PlaceExtensionTypeEnum.link &&
        <ListItemLink />
      }

      {
        placeRelatedState.show &&
        <ListPlaceRelated />
      }
      <ShowPositionSelect />
      <ShowSuggestPlace />
    </div>
  )
}

PlaceDetail.propTypes = {
  //
};

export default PlaceDetail;
